import { GameState, getAllLegalMoves, validateMove, getGameStatus, type Position, type Piece, type PieceType } from './chess-engine';
import { getOpeningMove, getFenFromGameState } from './opening-book';
import { createBotWorker, type WorkerMessage, type WorkerResponse } from './bot-worker';

export interface Move {
  from: Position;
  to: Position;
  promotion?: PieceType;
}

export interface BotPersonality {
  name: string;
  elo: number;
  thinkingTime: number; // ms
  blunderRate: number; // 0-1, probability of making a bad move
  tacticalStrength: number; // 0-1, ability to find tactical shots
  positionalUnderstanding: number; // 0-1, understanding of positional concepts
  endgameKnowledge: number; // 0-1, endgame technique
  openingKnowledge: number; // 0-1, opening theory knowledge
  maxDepth: number; // Maximum search depth
}

// Bot worker instance (singleton)
let botWorker: Worker | null = null;

export function getBotWorker(): Worker {
  if (!botWorker) {
    botWorker = createBotWorker();
  }
  return botWorker;
}

const botPersonalities: Record<number, BotPersonality> = {
  200: {
    name: "Ella",
    elo: 200,
    thinkingTime: 500,
    blunderRate: 0.4,
    tacticalStrength: 0.1,
    positionalUnderstanding: 0.05,
    endgameKnowledge: 0.1,
    openingKnowledge: 0.1,
    maxDepth: 2
  },
  400: {
    name: "Marty", 
    elo: 400,
    thinkingTime: 500,
    blunderRate: 0.6,
    tacticalStrength: 0.1,
    positionalUnderstanding: 0.05,
    endgameKnowledge: 0.1,
    openingKnowledge: 0.1,
    maxDepth: 1
  },
  700: {
    name: "Sophie",
    elo: 700,
    thinkingTime: 600,
    blunderRate: 0.25,
    tacticalStrength: 0.2,
    positionalUnderstanding: 0.1,
    endgameKnowledge: 0.15,
    openingKnowledge: 0.2,
    maxDepth: 2
  },
  900: {
    name: "Carlos", 
    elo: 900,
    thinkingTime: 800,
    blunderRate: 0.18,
    tacticalStrength: 0.35,
    positionalUnderstanding: 0.2,
    endgameKnowledge: 0.25,
    openingKnowledge: 0.3,
    maxDepth: 3
  },
  1200: {
    name: "Nina",
    elo: 1200,
    thinkingTime: 1000,
    blunderRate: 0.12,
    tacticalStrength: 0.4,
    positionalUnderstanding: 0.4,
    endgameKnowledge: 0.35,
    openingKnowledge: 0.5,
    maxDepth: 3
  },
  1400: {
    name: "Jason",
    elo: 1400, 
    thinkingTime: 1500,
    blunderRate: 0.08,
    tacticalStrength: 0.7,
    positionalUnderstanding: 0.5,
    endgameKnowledge: 0.6,
    openingKnowledge: 0.7,
    maxDepth: 4
  },
  2000: {
    name: "Aisha",
    elo: 2000,
    thinkingTime: 2000,
    blunderRate: 0.03,
    tacticalStrength: 0.85,
    positionalUnderstanding: 0.75,
    endgameKnowledge: 0.8,
    openingKnowledge: 0.85,
    maxDepth: 5
  },
  2800: {
    name: "Viktor",
    elo: 2800,
    thinkingTime: 2300,
    blunderRate: 0.01,
    tacticalStrength: 0.95,
    positionalUnderstanding: 0.9,
    endgameKnowledge: 0.93,
    openingKnowledge: 0.92,
    maxDepth: 6
  },
  3200: {
    name: "Magnus",
    elo: 3200,
    thinkingTime: 2500,
    blunderRate: 0.005,
    tacticalStrength: 0.98,
    positionalUnderstanding: 0.95,
    endgameKnowledge: 0.98,
    openingKnowledge: 0.95,
    maxDepth: 6
  }
};

// Fallback simple evaluation for very weak bots or when worker fails
function simpleEvaluate(gameState: GameState): number {
  const PIECE_VALUES = {
    'p': 100, 'n': 300, 'b': 320, 'r': 500, 'q': 900, 'k': 10000,
    'P': 100, 'N': 300, 'B': 320, 'R': 500, 'Q': 900, 'K': 10000
  };
  
  let score = 0;
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const piece = gameState.board[row][col];
      if (piece) {
        const value = PIECE_VALUES[piece.type.toLowerCase()];
        score += piece.color === 'white' ? value : -value;
      }
    }
  }
  
  return gameState.sideToMove === 'black' ? -score : score;
}

function getRandomMove(gameState: GameState): Move | null {
  const legalMoves = getAllLegalMoves(gameState);
  return legalMoves.length > 0 
    ? legalMoves[Math.floor(Math.random() * legalMoves.length)]
    : null;
}

function getWeakBotMove(gameState: GameState, personality: BotPersonality): Move | null {
  const legalMoves = getAllLegalMoves(gameState);
  if (legalMoves.length === 0) return null;
  
    // For very weak bots, often play random moves
    if (Math.random() < 0.5) { // Increased from 0.3 - more random moves
      return legalMoves[Math.floor(Math.random() * legalMoves.length)];
    }
  
  // Simple evaluation of moves
  const evaluatedMoves = legalMoves.map(move => {
    const moveResult = validateMove(gameState, move.from, move.to, move.promotion);
    if (!moveResult.isLegal || !moveResult.newState) {
      return { move, score: -Infinity };
    }
    
    let score = -simpleEvaluate(moveResult.newState);
    
    // Check for captures
    const targetPiece = gameState.board[move.to[0]][move.to[1]];
    if (targetPiece) {
      score += 50; // Simple capture bonus
    }
    
    // Add lots of randomness for weak bots
    score += (Math.random() - 0.5) * 300;
    
    return { move, score };
  });
  
  evaluatedMoves.sort((a, b) => b.score - a.score);
  
  // High blunder rate - often pick a bad move
  if (Math.random() < personality.blunderRate) {
    const worstHalf = Math.floor(evaluatedMoves.length / 2);
    const randomIndex = worstHalf + Math.floor(Math.random() * (evaluatedMoves.length - worstHalf));
    return evaluatedMoves[Math.min(randomIndex, evaluatedMoves.length - 1)].move;
  }
  
  return evaluatedMoves[0].move;
}

export function getBotMove(gameState: GameState, botElo: number): Promise<Move | null> {
  return new Promise((resolve, reject) => {
    // Check if game is still playable (ongoing or in check)
    const status = getGameStatus(gameState);
    if (status.status !== 'ongoing' && status.status !== 'check') {
      console.log('Game not playable, bot returning null');
      resolve(null);
      return;
    }
    
    const personality = botPersonalities[botElo];
    
    if (!personality) {
      // Fallback to random move if ELO not found
      setTimeout(() => resolve(getRandomMove(gameState)), 800);
      return;
    }
    
    // For very weak bots, use simplified logic
    if (personality.elo <= 1000) { // Increased from 400 to 1000
      setTimeout(() => {
        resolve(getWeakBotMove(gameState, personality));
      }, personality.thinkingTime);
      return;
    }
    
    // Use Web Worker for stronger bots
    try {
      const worker = getBotWorker();
      const timeLimit = Math.min(personality.thinkingTime, 5000); // Max 5 seconds
      
      const timeout = setTimeout(() => {
        // Fallback if worker takes too long
        resolve(getRandomMove(gameState));
      }, timeLimit + 1000);
      
      const messageHandler = (e: MessageEvent<WorkerResponse>) => {
        if (e.data.type === 'MOVE_CALCULATED') {
          clearTimeout(timeout);
          worker.removeEventListener('message', messageHandler);
          resolve(e.data.move);
        }
      };
      
      worker.addEventListener('message', messageHandler);
      
      const message: WorkerMessage = {
        type: 'CALCULATE_MOVE',
        gameState,
        botElo,
        timeLimit
      };
      
      worker.postMessage(message);
      
    } catch (error) {
      console.warn('Bot worker failed, using fallback:', error);
      // Fallback to simple evaluation
      setTimeout(() => {
        resolve(getWeakBotMove(gameState, personality));
      }, personality.thinkingTime);
    }
  });
}

export function getBotPersonality(elo: number): BotPersonality | null {
  return botPersonalities[elo] || null;
}

export function getAvailableBotElos(): number[] {
  return Object.keys(botPersonalities).map(Number).sort((a, b) => a - b);
}
